import React, { useState } from 'react';
import { Stack, Typography, IconButton, Tooltip, Snackbar } from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  IosShare as ShareIcon,
} from '@mui/icons-material';
import { SITE_URL } from './Seo';
import { trackEvent } from '../utils/analytics';

/**
 * Lightweight social share row. Uses the native share sheet on supported
 * devices and falls back to per-network share links + copy-to-clipboard.
 */
const ShareButtons = ({ title = 'InspectIQ', path = '/', label = 'Share this page' }) => {
  const [copied, setCopied] = useState(false);
  const url = `${SITE_URL}${path === '/' ? '' : path}`;
  const text = `${title} — InspectIQ`;

  const share = (network) => {
    trackEvent('share', { method: network, content: path });
  };

  const nativeShare = async () => {
    share('native');
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
      } catch (e) {
        /* user cancelled */
      }
    }
  };

  const copyLink = async () => {
    share('copy_link');
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  const links = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    email: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`I thought this might be useful: ${url}`)}`,
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
        {label}
      </Typography>

      {canNativeShare && (
        <Tooltip title="Share">
          <IconButton onClick={nativeShare} size="small" aria-label="Share" sx={{ color: 'primary.main' }}>
            <ShareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Share on WhatsApp">
        <IconButton
          component="a"
          href={links.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => share('whatsapp')}
          size="small"
          aria-label="Share on WhatsApp"
          sx={{ color: 'primary.main' }}
        >
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share on LinkedIn">
        <IconButton
          component="a"
          href={links.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => share('linkedin')}
          size="small"
          aria-label="Share on LinkedIn"
          sx={{ color: 'primary.main' }}
        >
          <LinkedInIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share by email">
        <IconButton
          component="a"
          href={links.email}
          onClick={() => share('email')}
          size="small"
          aria-label="Share by email"
          sx={{ color: 'primary.main' }}
        >
          <EmailIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Copy link">
        <IconButton onClick={copyLink} size="small" aria-label="Copy link" sx={{ color: 'primary.main' }}>
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
};

export default ShareButtons;
